import React from 'react';
import { Box, Card, CardContent, Typography, Grid } from '@mui/material';
import useSWR from 'swr';
import { PATH_NAME } from '../../../configs/pathName';
import { GetUserCoupon } from '../../../api/VoucherAPI';
import voucherPageStyles from '../../../styles/ProtectedPage/vouchers';
import assets from '../../../assets';
import { generalFetcher } from '../../../helpers/Fetchers/fetchers';
import { typo } from '../../../assets/fonts/font';
import colorConfigs from '../../../configs/colorConfigs';

const toMinutes = (time: string) => {
  if (!time || typeof time !== 'string') return 0;

  const [hours, minutes] = time.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;

  return hours * 60 + minutes;
};

const isTimeInRange = (currentTime: string, startTime: string, endTime: string) => {
  const currentMinutes = toMinutes(currentTime);
  const startMinutes = toMinutes(startTime);
  const endMinutes = toMinutes(endTime);

  if (startMinutes <= endMinutes) {
    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  }

  return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
};

const getVoucherTimeRules = (voucher: any) => {
  const timeRules = voucher?.allowed_time || voucher?.member_group_coupon?.allowed_time || {};

  return {
    allowRules: Array.isArray(timeRules.allow) ? timeRules.allow : [],
    disallowRules: Array.isArray(timeRules.disallow) ? timeRules.disallow : [],
  };
};

const formatTimeRange = (rule: any) => {
  if (!rule || !rule.start_time || !rule.end_time) return '';
  return `${rule.start_time} - ${rule.end_time}`;
};

const getTimeRuleLabel = (voucher: any) => {
  const { allowRules, disallowRules } = getVoucherTimeRules(voucher);

  if (allowRules.length && disallowRules.length) {
    return `Allowed: ${allowRules.map(formatTimeRange).join(', ')} | Restricted: ${disallowRules.map(formatTimeRange).join(', ')}`;
  }

  if (allowRules.length) {
    return `Allowed: ${allowRules.map(formatTimeRange).join(', ')}`;
  }

  if (disallowRules.length) {
    return `Restricted: ${disallowRules.map(formatTimeRange).join(', ')}`;
  }

  return '';
};

const isVoucherUsableNow = (voucher: any) => {
  const { allowRules, disallowRules } = getVoucherTimeRules(voucher);

  const currentTime = new Date().toLocaleTimeString('en-GB', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });

  const isInsideAllowWindow = allowRules.length
    ? allowRules.some((rule: any) => isTimeInRange(currentTime, rule.start_time, rule.end_time))
    : true;

  const isInsideDisallowWindow = disallowRules.some((rule: any) =>
    isTimeInRange(currentTime, rule.start_time, rule.end_time)
  );

  if (allowRules.length && disallowRules.length) {
    return isInsideAllowWindow && !isInsideDisallowWindow;
  }

  if (allowRules.length) {
    return isInsideAllowWindow;
  }

  if (disallowRules.length) {
    return !isInsideDisallowWindow;
  }

  return true;
};

const VouchersPage: React.FC = () => {
  const { data: profile } = useSWR(PATH_NAME.API_AUTH_PROFILE, generalFetcher);
  
  const { data: userCoupons, error, isLoading } = useSWR(
    profile ? PATH_NAME.API_GET_USER_VOUCHERS : null, 
    GetUserCoupon
  );

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading vouchers</div>;

  return (
    <Box sx={voucherPageStyles.container}>
      <Typography variant="h6" sx={{...typo.title ,...voucherPageStyles.title}}>My Vouchers</Typography>

      <Grid container spacing={2}>
        {userCoupons && userCoupons.length > 0 ? (
          userCoupons.map((coupon: any) => {
            const isUsable = isVoucherUsableNow(coupon);
            const timeRuleLabel = getTimeRuleLabel(coupon);

            return (
              <Grid item xs={12} sm={6} md={4} key={coupon.id} sx={{ display: 'flex' }}>
                <Card
                  sx={{
                    ...voucherPageStyles.card,
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    opacity: isUsable ? 1 : 0.72,
                    filter: isUsable ? 'none' : 'saturate(0.65) brightness(0.98)',
                    backgroundColor: isUsable ? voucherPageStyles.card.backgroundColor : colorConfigs.mutedBackground,
                    borderColor: isUsable ? colorConfigs.tertiary : colorConfigs.disabledBorder,
                  }}
                >
                  <CardContent sx={voucherPageStyles.cardContent}>
                    <Box sx={voucherPageStyles.logoBox}>
                      <img src={assets.images.logo} alt="Logo" style={{width:'100%'}} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="h6" sx={{...typo.sectionHeadline, ...voucherPageStyles.couponName}}>{coupon.name}</Typography>
                      <Typography variant="body2" sx={{...typo.bodyContent, ...voucherPageStyles.couponDetails}}>
                        Code: {coupon.code}
                      </Typography>
                      <Typography variant="body1" sx={{...typo.bodyContent, ...voucherPageStyles.couponDetails, lineHeight: 1.3}}>
                        {coupon.discount_type === 'percentage' ? (
                          <>
                            <Typography component="span" sx={{...typo.bodyContent, ...voucherPageStyles.discountText}}>
                              {coupon.discount_amount}%
                            </Typography>{' '}
                             off
                          </>
                        ) : (
                          <>
                            <Typography component="span" sx={{...typo.bodyContent, ...voucherPageStyles.discountText}}>
                              ${coupon.discount_amount}
                            </Typography>{' '}
                            coupon
                          </>
                        )}
                      </Typography>
                        <Typography variant="body2" sx={{...voucherPageStyles.minimumSpendingText, lineHeight: 1.3}}>
                          {coupon.minimum_spending ? `Any Purchase of $${coupon.minimum_spending} or more.` : 'No minimum spending'}
                        </Typography>
                        <Typography variant="body2" sx={{...voucherPageStyles.minimumSpendingText, lineHeight: 1.3}}>
                          {coupon.expiry_date ? `Expires on ${coupon.expiry_date}` : 'No expiry date'}
                        </Typography>
                        {timeRuleLabel && (
                          <Typography variant="caption" sx={voucherPageStyles.timeRuleText}>
                            {timeRuleLabel}
                          </Typography>
                        )}
                        {!isUsable && (
                          <Typography variant="caption" sx={voucherPageStyles.unavailableText}>
                            Unavailable at this time
                          </Typography>
                        )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        ) : (
          <Typography variant="body2" sx={typo.bodyContent} color="textSecondary">
            No coupons available
          </Typography>
        )}
      </Grid>
    </Box>
  );
};

export default VouchersPage;
